import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import kioskAssignmentApi, {
  KioskDto,
} from '../../../../../../services/kioskAssignmentApiService';

/**
 * 🖥️ Plan 2 — Kiosk Cihazları tab.
 *
 * Hızlı satış ekranına bağlı kioskları yönetir.
 * - Üst kısımda atanmış kiosklar `Chip` listesi (her chip'te onDelete ile unassign).
 * - Alt kısımda şubedeki tüm kioskları içeren `Autocomplete multiple` + Kaydet butonu.
 * - 1:N mantığı backend tarafında: assign-screen verilen ID'leri set, listede olmayan
 *   öncekileri unset eder. Yeni atanan kiosk başka screen'e bağlıysa eski bağ otomatik silinir.
 */
interface KioskAssignmentTabProps {
  screenId: number;
  companyId: string;
  branchId: string;
}

const KioskAssignmentTab: React.FC<KioskAssignmentTabProps> = ({
  screenId,
  companyId, // eslint-disable-line @typescript-eslint/no-unused-vars
  branchId,
}) => {
  const { t } = useTranslation('salesSystem');

  const [branchKiosks, setBranchKiosks] = useState<KioskDto[]>([]);
  const [assignedKiosks, setAssignedKiosks] = useState<KioskDto[]>([]);
  const [selected, setSelected] = useState<KioskDto[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (!screenId || !branchId) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const [byBranch, byScreen] = await Promise.all([
        kioskAssignmentApi.getByBranch(branchId),
        kioskAssignmentApi.getByScreen(screenId),
      ]);
      setBranchKiosks(byBranch);
      setAssignedKiosks(byScreen);
      // Atanmış olanları autocomplete'in default seçimi yap
      const assignedIds = new Set(byScreen.map((k) => k.id));
      setSelected(byBranch.filter((k) => assignedIds.has(k.id)));
    } catch (err: any) {
      console.error('[KioskAssignmentTab] load hata:', err);
      setError(err?.response?.data?.message || err?.message || t('kioskAssignmentTab.errors.loadFailed'));
      setBranchKiosks([]);
      setAssignedKiosks([]);
      setSelected([]);
    } finally {
      setLoading(false);
    }
  }, [screenId, branchId, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Değişiklik var mı? (Save butonu disable kararı için)
  const hasChanges = useMemo(() => {
    const a = assignedKiosks.map((k) => k.id);
    const b = new Set(selected.map((k) => k.id));
    if (a.length !== b.size) return true;
    for (let i = 0; i < a.length; i++) {
      if (!b.has(a[i])) return true;
    }
    return false;
  }, [assignedKiosks, selected]);

  const handleSave = async () => {
    if (!screenId || saving) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await kioskAssignmentApi.assignKiosksToScreen({
        KioskIds: selected.map((k) => k.id),
        QuickSaleScreenId: screenId,
      });
      setInfo(t('kioskAssignmentTab.saved'));
      await loadAll();
    } catch (err: any) {
      console.error('[KioskAssignmentTab] save hata:', err);
      setError(err?.response?.data?.message || err?.message || t('kioskAssignmentTab.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveChip = async (kioskId: number) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await kioskAssignmentApi.unassignKiosk({ KioskId: kioskId });
      setInfo(t('kioskAssignmentTab.removed'));
      await loadAll();
    } catch (err: any) {
      console.error('[KioskAssignmentTab] unassign hata:', err);
      setError(err?.response?.data?.message || err?.message || t('kioskAssignmentTab.errors.removeFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!branchId) {
    return (
      <Alert severity="info">{t('kioskAssignmentTab.errors.noBranch')}</Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DesktopWindowsIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('kioskAssignmentTab.title')}
          </Typography>
        </Box>
        <Tooltip title={t('kioskAssignmentTab.refresh')}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadAll}
              disabled={loading || saving}
            >
              {t('kioskAssignmentTab.refresh')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('kioskAssignmentTab.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {/* Atanmış kiosklar — chip listesi */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('kioskAssignmentTab.assignedSection')}
            </Typography>
            {assignedKiosks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('kioskAssignmentTab.noneAssigned')}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {assignedKiosks.map((k) => (
                  <Chip
                    key={k.id}
                    icon={<DesktopWindowsIcon />}
                    label={k.name || `Kiosk #${k.id}`}
                    color="primary"
                    variant="filled"
                    onDelete={() => handleRemoveChip(k.id)}
                    disabled={saving}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Yeni atama — autocomplete + kaydet */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('kioskAssignmentTab.selectSection')}
            </Typography>

            {branchKiosks.length === 0 ? (
              <Alert severity="warning">
                {t('kioskAssignmentTab.errors.noKiosksInBranch')}
              </Alert>
            ) : (
              <>
                <Autocomplete
                  multiple
                  options={branchKiosks}
                  value={selected}
                  onChange={(_, newValue) => setSelected(newValue)}
                  getOptionLabel={(option) => option.name || `Kiosk #${option.id}`}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  disabled={saving}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('kioskAssignmentTab.autocompleteLabel')}
                      placeholder={t('kioskAssignmentTab.autocompletePlaceholder')}
                      size="small"
                    />
                  )}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {saving ? t('kioskAssignmentTab.saving') : t('kioskAssignmentTab.save')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default KioskAssignmentTab;
